import { useState, useRef } from "react";
import {
  View,
  Text,
  Pressable,
  Modal,
  Animated,
  Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Profile } from "@/types";

const { height: SCREEN_HEIGHT } = Dimensions.get("window");

/**
 * Props for UserSwitcher component
 */
interface UserSwitcherProps {
  /**
   * Optional custom style for the container
   */
  containerStyle?: object;
}

/**
 * Dependent user item in the dropdown
 */
interface DependentItemProps {
  dependent: Profile;
  onSelect: () => void;
  colors: ReturnType<typeof useThemeColors>;
}

const DependentItem = ({ dependent, onSelect, colors }: DependentItemProps) => (
  <Pressable
    onPress={onSelect}
    className="flex-row items-center px-4 py-3 active:opacity-70"
    style={{
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    }}
    accessibilityRole="button"
    accessibilityLabel={`Inloggen als ${dependent.full_name}`}
  >
    <View
      className="w-10 h-10 rounded-full items-center justify-center mr-3"
      style={{ backgroundColor: "#DBEAFE" }}
    >
      <Text className="font-bold" style={{ color: "#1D4ED8" }}>
        {(dependent.full_name || "?").charAt(0).toUpperCase()}
      </Text>
    </View>
    <View className="flex-1">
      <Text className="font-medium" style={{ color: colors.text }}>
        Login als {dependent.full_name}
      </Text>
      <Text className="text-xs" style={{ color: colors.textTertiary }}>
        Kind
      </Text>
    </View>
    <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
  </Pressable>
);

/**
 * UserSwitcher Component
 * 
 * Displays the current user's name in the header with the ability to switch
 * to dependent users (kids without their own phone).
 * 
 * When switched:
 * - Shows the dependent's name
 * - Shows a "Terug" button to switch back to the guardian
 */
export function UserSwitcher({ containerStyle }: UserSwitcherProps) {
  const {
    profile,
    activeProfile,
    dependents,
    isSwitched,
    switchToDependent,
    switchBackToSelf,
  } = useAuth();
  const colors = useThemeColors();
  
  const [showDropdown, setShowDropdown] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  /**
   * Open the dropdown with animation
   */
  const handleOpenDropdown = () => {
    if (dependents.length === 0) return;
    
    setShowDropdown(true);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();
  };

  /**
   * Close the dropdown with animation
   */
  const handleCloseDropdown = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 150,
      useNativeDriver: true,
    }).start(() => {
      setShowDropdown(false);
    });
  };

  /**
   * Handle selecting a dependent
   */
  const handleSelectDependent = (dependentId: string) => {
    switchToDependent(dependentId);
    handleCloseDropdown();
  };

  /**
   * Handle switching back to self
   */
  const handleSwitchBack = () => {
    switchBackToSelf();
  };

  // Don't render if no profile
  if (!profile || !activeProfile) return null;

  // When switched to a dependent - show simple view without "Terug" button
  // The PlayingAsBanner already provides the "Terug" button at the top of the screen
  if (isSwitched) {
    return (
      <View className="flex-row items-center" style={containerStyle}>
        {/* Dependent Avatar */}
        <View
          className="w-8 h-8 rounded-full items-center justify-center mr-2"
          style={{ backgroundColor: "#DBEAFE" }}
        >
          <Text className="font-bold text-sm" style={{ color: "#1D4ED8" }}>
            {(activeProfile.full_name || "?").charAt(0).toUpperCase()}
          </Text>
        </View>

        {/* Dependent Name */}
        <Text className="font-semibold" style={{ color: colors.text }}>
          {activeProfile.full_name}
        </Text>
      </View>
    );
  }

  // Normal state - show own profile with optional dropdown
  return (
    <>
      <Pressable
        onPress={handleOpenDropdown}
        disabled={dependents.length === 0}
        className="flex-row items-center"
        style={containerStyle}
        accessibilityRole={dependents.length > 0 ? "button" : "text"}
        accessibilityLabel={
          dependents.length > 0
            ? `${activeProfile.full_name}, klik om te wisselen`
            : activeProfile.full_name || "Profiel"
        }
      >
        {/* User Avatar */}
        <View
          className="w-8 h-8 rounded-full items-center justify-center mr-2"
          style={{ backgroundColor: colors.primaryLight }}
        >
          <Text className="font-bold text-sm" style={{ color: colors.primary }}>
            {(activeProfile.full_name || "?").charAt(0).toUpperCase()}
          </Text>
        </View>

        {/* User Name */}
        <Text className="font-semibold" style={{ color: colors.text }}>
          Hoi, {activeProfile.full_name?.split(" ")[0] || ""}
        </Text>

        {/* Dropdown Indicator */}
        {dependents.length > 0 && (
          <Ionicons
            name="chevron-down"
            size={16}
            color={colors.textSecondary}
            style={{ marginLeft: 4 }}
          />
        )}
      </Pressable>

      {/* Dropdown Modal */}
      <Modal
        visible={showDropdown}
        transparent
        animationType="none"
        onRequestClose={handleCloseDropdown}
      >
        <Pressable
          className="flex-1"
          onPress={handleCloseDropdown}
          accessibilityRole="button"
          accessibilityLabel="Sluit menu"
        >
          <Animated.View
            className="flex-1"
            style={{
              backgroundColor: "rgba(0,0,0,0.3)",
              opacity: fadeAnim,
            }}
          />
        </Pressable>

        <Animated.View
          style={{
            position: "absolute",
            top: 100,
            left: 16,
            right: 16,
            backgroundColor: colors.surface,
            borderRadius: 16,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.15,
            shadowRadius: 12,
            elevation: 8,
            opacity: fadeAnim,
            transform: [
              {
                translateY: fadeAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-20, 0],
                }),
              },
            ],
          }}
        >
          {/* Header */}
          <View
            className="px-4 py-3 flex-row items-center justify-between"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <Text className="font-semibold" style={{ color: colors.text }}>
              Inloggen als...
            </Text>
            <Pressable onPress={handleCloseDropdown}>
              <Ionicons name="close" size={20} color={colors.textSecondary} />
            </Pressable>
          </View>

          {/* Dependents List */}
          {dependents.map((dependent) => (
            <DependentItem
              key={dependent.id}
              dependent={dependent}
              onSelect={() => handleSelectDependent(dependent.id)}
              colors={colors}
            />
          ))}

          {/* Info Footer */}
          <View className="px-4 py-3" style={{ backgroundColor: colors.background }}>
            <Text className="text-xs text-center" style={{ color: colors.textTertiary }}>
              Je kunt inloggen als een kind om namens hen mee te doen
            </Text>
          </View>
        </Animated.View>
      </Modal>
    </>
  );
}

export default UserSwitcher;

